import type { IndustryCategory } from '../../../types';
import type {
  ExpansionPackDefinition,
  GrowthPackDefinition,
  IndustryContentRequest,
  IndustryContentSnapshot,
  IndustryPackDefinition,
} from './types';

function isGrowthTierUnlocked(
  growth: GrowthPackDefinition,
  request: IndustryContentRequest,
): boolean {
  if (growth.tier === 1) return true;

  const cond = growth.unlockConditions;
  if (!cond) return true;

  if (cond.minCompliance !== undefined && (request.stats?.compliance ?? 0) < cond.minCompliance) {
    return false;
  }
  if (cond.minBudget !== undefined && (request.stats?.budget ?? 0) < cond.minBudget) {
    return false;
  }
  const completedMissions = new Set(request.completedMissionIds ?? []);
  if (cond.completedMissionIds?.some((id) => !completedMissions.has(id))) {
    return false;
  }
  const completedScenarios = new Set(request.completedScenarioIds ?? []);
  if (cond.completedScenarioIds?.some((id) => !completedScenarios.has(id))) {
    return false;
  }
  return true;
}

function isExpansionActive(
  expansion: ExpansionPackDefinition,
  activeGrowthIds: Set<string>,
  installed: Set<string>,
  activeExpansionIds: Set<string>,
): boolean {
  if (!installed.has(expansion.id)) return false;
  if (expansion.requiresGrowthTierId && !activeGrowthIds.has(expansion.requiresGrowthTierId)) {
    return false;
  }
  for (const req of expansion.requiresExpansionIds ?? []) {
    if (!activeExpansionIds.has(req)) return false;
  }
  return true;
}

export function resolveIndustryContent(
  pack: IndustryPackDefinition,
  request: IndustryContentRequest,
): IndustryContentSnapshot {
  const installed = new Set(request.installedExpansionIds ?? []);
  const activeGrowthTierIds = pack.growthPacks
    .filter((g) => isGrowthTierUnlocked(g, request))
    .map((g) => g.id);

  const activeGrowthSet = new Set(activeGrowthTierIds);
  const activeExpansionIds: string[] = [];
  for (const expansion of pack.expansionPacks) {
    if (isExpansionActive(expansion, activeGrowthSet, installed, new Set(activeExpansionIds))) {
      activeExpansionIds.push(expansion.id);
    }
  }

  const scenarios = [...pack.scenarios];
  const products = [...pack.products];
  const missions = [...pack.missions];
  const frameworkIds = new Set(pack.frameworks.map((f) => f.id));

  for (const expansionId of activeExpansionIds) {
    const expansion = pack.expansionPacks.find((e) => e.id === expansionId);
    if (!expansion) continue;
    scenarios.push(...(expansion.adds.scenarios ?? []));
    products.push(...(expansion.adds.products ?? []));
    missions.push(...(expansion.adds.missions ?? []));
    for (const fid of expansion.adds.frameworkIds ?? []) frameworkIds.add(fid);
  }

  const unlockedProductIds = new Set<string>();
  const unlockedMissionIds = new Set<string>();
  const unlockedScenarioIds = new Set<string>();

  for (const growthId of activeGrowthTierIds) {
    const growth = pack.growthPacks.find((g) => g.id === growthId);
    if (!growth) continue;
    for (const id of growth.unlocks.productIds ?? []) unlockedProductIds.add(id);
    for (const id of growth.unlocks.missionIds ?? []) unlockedMissionIds.add(id);
    for (const id of growth.unlocks.scenarioIds ?? []) unlockedScenarioIds.add(id);
  }

  // Sandbox / full catalog: if no growth packs, everything is unlocked
  if (pack.growthPacks.length === 0) {
    products.forEach((p) => unlockedProductIds.add(p.id));
    missions.forEach((m) => unlockedMissionIds.add(m.id));
    scenarios.forEach((s) => unlockedScenarioIds.add(s.id));
  }

  const frameworks =
    pack.id === 'sandbox'
      ? pack.frameworks
      : pack.frameworks.filter((f) => frameworkIds.has(f.id));

  return {
    industryId: pack.id,
    category: pack.category,
    scenarios,
    products,
    missions,
    frameworks,
    activeGrowthTierIds,
    activeExpansionIds,
    unlockedProductIds: [...unlockedProductIds],
    unlockedMissionIds: [...unlockedMissionIds],
    unlockedScenarioIds: [...unlockedScenarioIds],
  };
}

export function mergeSandboxPacks(packs: IndustryPackDefinition[]): IndustryPackDefinition {
  const playable = packs.filter((p) => p.id !== 'sandbox');
  return {
    id: 'sandbox',
    version: '1.0.0',
    category: packs.find((p) => p.id === 'sandbox')!.category,
    scenarios: playable.flatMap((p) => p.scenarios),
    products: playable.flatMap((p) => p.products),
    missions: playable.flatMap((p) => p.missions),
    frameworks: packs.find((p) => p.id === 'sandbox')!.frameworks,
    growthPacks: [],
    expansionPacks: playable.flatMap((p) => p.expansionPacks),
  };
}

export function getPackForIndustry(
  packs: IndustryPackDefinition[],
  industryId: IndustryCategory,
): IndustryPackDefinition | undefined {
  if (industryId === 'sandbox') {
    return mergeSandboxPacks(packs);
  }
  return packs.find((p) => p.id === industryId);
}